
interface StructuredDataProps {
  data: Record<string, unknown>
}

/**
 * Serialize JSON-LD safely for inline injection. Escapes `<` (and U+2028/2029)
 * so user-controlled fields (e.g. a pro's companyName) cannot break out of the
 * <script> with `</script>` or inject markup. Without this, a companyName like
 * `</script><img src=x onerror=…>` executes (CSP currently allows inline).
 */
export function safeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
}

export function StructuredData({ data }: StructuredDataProps) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }}
    />
  )
}

export function WebsiteSchema() {
  return (
    <StructuredData
      data={{
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'AfficheServices',
        url: 'https://www.afficheservices.fr',
        description: "Plateforme de mise en relation entre particuliers et artisans vérifiés en France.",
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://www.afficheservices.fr/recherche?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      }}
    />
  )
}

// Map our service category slug → schema.org LocalBusiness specific subtype.
// Subtypes render richer SERP treatments (industry-specific panels).
const CATEGORY_SCHEMA_TYPE: Record<string, string> = {
  plomberie: 'Plumber',
  electricite: 'Electrician',
  peinture: 'HousePainter',
  renovation: 'GeneralContractor',
  menuiserie: 'GeneralContractor',
  toiture: 'RoofingContractor',
  'couverture-zinguerie': 'RoofingContractor',
  serrurerie: 'Locksmith',
  nettoyage: 'HousePainter',
  jardinage: 'GeneralContractor',
  climatisation: 'HVACBusiness',
  demenagement: 'MovingCompany',
  'carrelage-sol': 'GeneralContractor',
  isolation: 'GeneralContractor',
  maconnerie: 'GeneralContractor',
  platrerie: 'GeneralContractor',
  piscine: 'GeneralContractor',
  securite: 'SecurityService',
  'panneaux-solaires': 'GeneralContractor',
  assainissement: 'GeneralContractor',
}

export function LocalBusinessSchema({ pro }: {
  pro: {
    companyName: string
    description?: string | null
    city: string
    postalCode: string
    addressLine1?: string | null
    ratingAvg?: string | null
    reviewCount: number
    slug: string
    latitude?: string | null
    longitude?: string | null
    logoUrl?: string | null
    coverImageUrl?: string | null
    primaryCategorySlug?: string | null
    primaryCategoryName?: string | null
    serviceAreaDepartments?: string[] | null
  }
}) {
  const address: Record<string, string> = { '@type': 'PostalAddress', addressCountry: 'FR' }
  if (pro.addressLine1) address.streetAddress = pro.addressLine1
  if (pro.city) address.addressLocality = pro.city
  if (pro.postalCode) address.postalCode = pro.postalCode

  const type = pro.primaryCategorySlug && CATEGORY_SCHEMA_TYPE[pro.primaryCategorySlug]
    ? CATEGORY_SCHEMA_TYPE[pro.primaryCategorySlug]
    : 'LocalBusiness'

  const lat = pro.latitude ? parseFloat(pro.latitude) : null
  const lng = pro.longitude ? parseFloat(pro.longitude) : null
  const hasGeo = lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)

  const image = pro.coverImageUrl || pro.logoUrl || null

  const areaServed = pro.serviceAreaDepartments && pro.serviceAreaDepartments.length > 0
    ? pro.serviceAreaDepartments.map((code) => ({ '@type': 'AdministrativeArea', name: `Département ${code}` }))
    : [{ '@type': 'Country', name: 'France' }]

  const profileUrl = `https://www.afficheservices.fr/pro/${pro.slug}`

  // Default availability — most artisan trades take inquiries during standard
  // business hours; emergency-only sub-categories (serrurerie, urgence routes)
  // can override via component prop in a future iteration. Whitespark 2026
  // factor #5: businesses open at search time rank higher in local pack.
  const openingHoursSpecification = [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '08:00', closes: '19:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '09:00', closes: '13:00' },
  ]

  return (
    <StructuredData
      data={{
        '@context': 'https://schema.org',
        '@type': type,
        '@id': `${profileUrl}#business`,
        name: pro.companyName,
        ...(pro.description ? { description: pro.description } : {}),
        address,
        url: profileUrl,
        mainEntityOfPage: profileUrl,
        // isPartOf links this business listing to the AfficheServices
        // aggregator entity → strongest signal for "Sites de lieux" carousel.
        isPartOf: { '@type': 'Organization', '@id': 'https://www.afficheservices.fr/#org', name: 'AfficheServices' },
        ...(hasGeo ? { geo: { '@type': 'GeoCoordinates', latitude: String(lat), longitude: String(lng) } } : {}),
        ...(image ? { image } : {}),
        priceRange: '€€',
        openingHoursSpecification,
        areaServed,
        // makesOffer = explicit Service catalog tied to this business.
        // Drives "[business] [service]" queries to surface this page.
        ...(pro.primaryCategoryName ? {
          makesOffer: [{
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: pro.primaryCategoryName,
              serviceType: pro.primaryCategoryName,
              provider: { '@type': 'Organization', name: pro.companyName },
              areaServed: areaServed.length === 1 ? areaServed[0] : areaServed,
            },
          }],
        } : {}),
        ...(pro.ratingAvg && pro.reviewCount > 0 && !isNaN(parseFloat(pro.ratingAvg)) && parseFloat(pro.ratingAvg) >= 1 ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: String(Math.min(5, Math.max(1, Math.round(parseFloat(pro.ratingAvg) * 10) / 10))),
            reviewCount: String(Math.max(1, pro.reviewCount)),
            bestRating: '5',
            worstRating: '1',
          },
        } : {}),
      }}
    />
  )
}

export function ReviewSchema({ reviews, pro }: {
  reviews: Array<{
    overallRating: number
    comment: string
    reviewerFirstName: string
    createdAt: string | Date
  }>
  pro: { companyName: string; slug: string; ratingAvg?: string | null; reviewCount?: number }
}) {
  // Filter out invalid reviews (rating must be 1-5)
  const validReviews = reviews.filter(r => r.overallRating >= 1 && r.overallRating <= 5 && r.comment)
  if (validReviews.length === 0) return null

  // Calculate aggregateRating — must be 1-5 range
  const avgRating = pro.ratingAvg
    ? parseFloat(pro.ratingAvg)
    : validReviews.reduce((sum, r) => sum + r.overallRating, 0) / validReviews.length
  const clampedRating = Math.min(5, Math.max(1, Math.round(avgRating * 10) / 10))

  // reviewCount must be positive (>= 1)
  const totalCount = Math.max(1, pro.reviewCount && pro.reviewCount > 0 ? pro.reviewCount : validReviews.length)

  // Ensure we never output NaN
  if (isNaN(clampedRating)) return null

  return (
    <StructuredData
      data={{
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: pro.companyName,
        url: `https://www.afficheservices.fr/pro/${pro.slug}`,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: String(clampedRating),
          reviewCount: String(totalCount),
          bestRating: '5',
          worstRating: '1',
        },
        review: validReviews.slice(0, 10).map((r) => ({
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: String(Math.min(5, Math.max(1, r.overallRating))),
            bestRating: '5',
            worstRating: '1',
          },
          author: {
            '@type': 'Person',
            name: r.reviewerFirstName || 'Client',
          },
          reviewBody: r.comment,
          datePublished: new Date(r.createdAt).toISOString().split('T')[0],
        })),
      }}
    />
  )
}

export function ServiceSchema({ category, priceBand, offerCount }: {
  category: { nameFr: string; slug: string; descriptionFr?: string | null; leadPriceMin?: string | null; leadPriceMax?: string | null }
  // Optional proprietary data (from city-service-stats fetcher) — beats
  // static SERVICE_PRICES because it reflects real accepted leads. Falls
  // back to category.leadPriceMin/Max when not available.
  priceBand?: { min: number; max: number; median: number } | null
  offerCount?: number | null
}) {
  // Prefer real lead data (priceBand) — gives Google live SERP price snippet.
  const lowPrice = priceBand?.min ?? (category.leadPriceMin ? parseFloat(category.leadPriceMin) : null)
  const highPrice = priceBand?.max ?? (category.leadPriceMax ? parseFloat(category.leadPriceMax) : null)

  const offers = lowPrice !== null && highPrice !== null && !isNaN(lowPrice) && !isNaN(highPrice)
    ? {
        '@type': 'AggregateOffer',
        lowPrice: String(lowPrice),
        highPrice: String(highPrice),
        priceCurrency: 'EUR',
        ...(offerCount && offerCount > 0 ? { offerCount: String(offerCount) } : {}),
        ...(priceBand?.median ? { price: String(priceBand.median) } : {}),
      }
    : null

  return (
    <StructuredData
      data={{
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: category.nameFr,
        description: category.descriptionFr ?? `Service de ${category.nameFr.toLowerCase()} — professionnels vérifiés`,
        url: `https://www.afficheservices.fr/services/${category.slug}`,
        provider: {
          '@type': 'Organization',
          name: 'AfficheServices',
          url: 'https://www.afficheservices.fr',
        },
        areaServed: { '@type': 'Country', name: 'France' },
        ...(offers ? { offers } : {}),
      }}
    />
  )
}

export function FAQSchema({ items }: { items: Array<{ question: string; answer: string }> }) {
  return (
    <StructuredData
      data={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        // Speakable specification — flags FAQ as voice-friendly so Google
        // Assistant / Siri / Alexa can read out questions and answers in voice
        // search responses. Targets `<details><summary>` elements that already
        // wrap each FAQ item across our routes (prix, services, alternatives,
        // vs, budget-travaux, comment-ca-marche, maprimenov, cee, blog).
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['details summary', 'details p'],
        },
        mainEntity: items.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      }}
    />
  )
}

export function OrganizationSchema() {
  return (
    <StructuredData
      data={{
        '@context': 'https://schema.org',
        // ProfessionalService + Organization dual-type: ProfessionalService
        // is what Google "Sites de lieux" / aggregator carousel rewards —
        // tells Google this is a directory/aggregator, not a single business.
        '@type': ['Organization', 'ProfessionalService'],
        '@id': 'https://www.afficheservices.fr/#org',
        name: 'AfficheServices',
        alternateName: 'Affiche Services',
        url: 'https://www.afficheservices.fr',
        logo: 'https://www.afficheservices.fr/favicon.svg',
        image: 'https://www.afficheservices.fr/favicon.svg',
        description: 'Plateforme de mise en relation entre particuliers et artisans vérifiés en France. Devis gratuits sous 48h. Plus de 110 000 professionnels référencés.',
        slogan: 'Trouvez votre artisan vérifié en 30 secondes',
        foundingDate: '2024',
        knowsLanguage: ['fr', 'fr-FR'],
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@afficheservices.fr',
          contactType: 'customer service',
          availableLanguage: 'French',
          areaServed: 'FR',
        },
        // Wider sameAs corpus = stronger Knowledge Graph signal.
        // Add the platform's verified social + business directory profiles.
        sameAs: [
          'https://facebook.com/afficheservices',
          'https://instagram.com/afficheservices',
          'https://linkedin.com/company/afficheservices',
          'https://twitter.com/afficheservices',
          'https://www.youtube.com/@afficheservices',
        ],
        areaServed: { '@type': 'Country', name: 'France', identifier: 'FR' },
        serviceArea: { '@type': 'Country', name: 'France' },
        priceRange: '€€',
        memberOf: { '@type': 'Organization', name: 'Plateformes de mise en relation' },
        // knowsAbout = explicit category coverage. Helps Google match
        // "plombier paris" / "electricien lyon" queries to this aggregator.
        knowsAbout: [
          'Plomberie', 'Électricité', 'Chauffage', 'Climatisation',
          'Menuiserie', 'Peinture', 'Maçonnerie', 'Couverture',
          'Isolation', 'Rénovation énergétique', 'Pompe à chaleur',
          'Salle de bain', 'Cuisine', 'Carrelage', 'Plâtrerie',
          'Serrurerie', 'Vitrerie', 'Jardinage', 'Paysagisme',
        ],
      }}
    />
  )
}

/**
 * ItemList — aggregator signal. Lists businesses on a city/category page.
 * Combined with CityPlaceSchema, tells Google "this URL = directory of N
 * verified pros in {city, category}", which is the entry ticket for
 * "Sites de lieux" carousel.
 */
export function ItemListSchema({ name, description, url, items }: {
  name: string
  description?: string
  url: string
  items: Array<{
    name: string
    url: string
    rating?: number | null
    reviewCount?: number | null
    image?: string | null
    city?: string | null
  }>
}) {
  if (items.length === 0) return null
  return (
    <StructuredData
      data={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        '@id': `${url}#list`,
        name,
        ...(description ? { description } : {}),
        url,
        numberOfItems: items.length,
        itemListOrder: 'https://schema.org/ItemListOrderDescending',
        itemListElement: items.map((it, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'LocalBusiness',
            name: it.name,
            url: it.url,
            ...(it.image ? { image: it.image } : {}),
            ...(it.city ? { address: { '@type': 'PostalAddress', addressLocality: it.city, addressCountry: 'FR' } } : {}),
            ...(it.rating && it.reviewCount && it.reviewCount > 0 ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: String(it.rating),
                reviewCount: String(it.reviewCount),
              },
            } : {}),
          },
        })),
      }}
    />
  )
}

/**
 * Geo meta tags — legacy but still indexed by Google for location-aware
 * SERP features. Use on city/pro pages alongside JSON-LD geo data.
 */
export function geoMeta(args: { region: string; placename: string; lat?: number | null; lon?: number | null }) {
  const meta: Array<{ name: string; content: string }> = [
    { name: 'geo.region', content: `FR-${args.region}` },
    { name: 'geo.placename', content: args.placename },
  ]
  if (args.lat != null && args.lon != null) {
    meta.push({ name: 'ICBM', content: `${args.lat}, ${args.lon}` })
    meta.push({ name: 'geo.position', content: `${args.lat};${args.lon}` })
  }
  return meta
}

export function BreadcrumbSchema({ items }: { items: Array<{ name: string; url: string }> }) {
  return (
    <StructuredData
      data={{
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: item.name,
          item: item.url,
        })),
      }}
    />
  )
}

/**
 * Place + Service + areaServed schema for programmatic city pages.
 * Targets Google "Sites de lieux" / location-aware result features.
 *
 * Combines:
 * - Place (the commune itself, with geo coordinates) — anchors the page
 *   to a real location entity so Google understands it's location-specific.
 * - Service with areaServed.City — declares the artisan service is
 *   delivered IN this place. This is what unlocks local SERP features.
 *
 * @param city - resolved geoFrance row + dept name
 * @param service - { name, slug } of the primary service
 * @param canonicalUrl - exact URL of this page
 * @param priceBand - optional real-lead price band (richer offers)
 */
/**
 * Wikipedia FR URL guess from city name. Many big FR communes resolve at
 * `fr.wikipedia.org/wiki/<UrlEncodedName>` directly. For collisions
 * (e.g. "Saint-Maur"), it 404s — Google ignores broken sameAs entries
 * rather than penalizing, so the cost of false positives is zero.
 */
function cityWikipediaUrl(name: string): string {
  return `https://fr.wikipedia.org/wiki/${encodeURIComponent(name.replace(/\s+/g, '_'))}`
}

export function CityPlaceSchema({ city, service, canonicalUrl, priceBand, offerCount, aggregateRating, wikidataUrl, communeImageUrl }: {
  city: {
    name: string
    postalCode: string
    dept: string
    deptName: string
    region: string
    lat: string | number
    lon: string | number
    population?: number | null
    alternateName?: string | null
    communeCode?: string | null
  }
  service: { name: string; slug: string }
  canonicalUrl: string
  priceBand?: { min: number; max: number; median: number } | null
  offerCount?: number | null
  // Aggregate rating across all pros in this (city, service) — drives
  // "4.7 ★ (124 avis)" rich snippet under SERP entry.
  aggregateRating?: { value: number; count: number } | null
  // Resolved server-side in the loader (from the INSEE code) and passed in, so
  // the ~250 KB commune-wikidata data map stays out of the client bundle.
  wikidataUrl?: string | null
  communeImageUrl?: string | null
}) {
  // Whitespark 2026 local pack guidance: geo coords need >= 5 decimal places
  // (~1.1 m precision). DB stores at scale 7 in geoFrance, but parseFloat +
  // JSON serialization drops trailing zeros (48.858930 → 48.85893 → "48.8589"
  // в SERP). Serialize as fixed-width STRING to preserve every decimal.
  // Schema.org accepts both number и string for GeoCoordinates.
  const rawLat = typeof city.lat === 'string' ? parseFloat(city.lat) : city.lat
  const rawLon = typeof city.lon === 'string' ? parseFloat(city.lon) : city.lon
  const lat = Number.isFinite(rawLat) ? (rawLat as number).toFixed(6) : null
  const lon = Number.isFinite(rawLon) ? (rawLon as number).toFixed(6) : null
  const placeId = `${canonicalUrl}#place`
  const serviceId = `${canonicalUrl}#service`
  const webPageId = `${canonicalUrl}#webpage`

  const offers = priceBand
    ? {
        '@type': 'AggregateOffer',
        lowPrice: String(priceBand.min),
        highPrice: String(priceBand.max),
        priceCurrency: 'EUR',
        ...(priceBand.median ? { price: String(priceBand.median) } : {}),
        ...(offerCount && offerCount > 0 ? { offerCount: String(offerCount) } : {}),
      }
    : null

  return (
    <StructuredData
      data={{
        '@context': 'https://schema.org',
        '@graph': [
          // WebPage + CollectionPage dual-type — explicit "this URL
          // collects businesses in this city" signal. CollectionPage is
          // what aggregator carousels look for.
          {
            '@type': ['WebPage', 'CollectionPage'],
            '@id': webPageId,
            url: canonicalUrl,
            name: `${service.name} à ${city.name}`,
            description: `Liste des ${service.name.toLowerCase()} vérifiés à ${city.name} (${city.dept})`,
            inLanguage: 'fr-FR',
            isPartOf: { '@id': 'https://www.afficheservices.fr/#org' },
            about: { '@id': placeId },
            mainEntity: { '@id': serviceId },
            speakable: { '@type': 'SpeakableSpecification', cssSelector: ['h1', 'h2', '.faq-summary'] },
          },
          {
            '@type': 'Place',
            '@id': placeId,
            name: city.name,
            ...(city.alternateName ? { alternateName: city.alternateName } : {}),
            // sameAs anchors this Place to its canonical entity in Google's
            // Knowledge Graph — the strongest "Sites de lieux" signal. Wikidata
            // (resolved from the INSEE code, exact match) is authoritative and
            // listed first; the Wikipedia URL (guessed from the name) is a
            // best-effort secondary that Google ignores if it 404s.
            sameAs: (() => {
              const wd = wikidataUrl ?? null
              return wd ? [wd, cityWikipediaUrl(city.name)] : [cityWikipediaUrl(city.name)]
            })(),
            address: {
              '@type': 'PostalAddress',
              addressLocality: city.name,
              postalCode: city.postalCode,
              addressRegion: city.deptName,
              addressCountry: 'FR',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: lat,
              longitude: lon,
            },
            // INSEE commune code + population as official entity anchors —
            // the INSEE code ties this Place to the canonical French commune
            // entity (data.gouv / Knowledge Graph), strengthening "Sites de
            // lieux" association.
            ...(() => {
              const props = [
                city.communeCode ? { '@type': 'PropertyValue', propertyID: 'INSEE', value: city.communeCode } : null,
                city.population ? { '@type': 'PropertyValue', propertyID: 'population', value: city.population } : null,
              ].filter(Boolean)
              return props.length ? { additionalProperty: props.length === 1 ? props[0] : props } : {}
            })(),
            // Real Wikimedia Commons photo of the commune (resolved from INSEE,
            // 99% coverage) — a genuine place image strengthens the "Sites de
            // lieux" visual far more than a generic card. Emitted as an
            // ImageObject whose `license`/`acquireLicensePage` point to the
            // Commons file page, where the exact author + CC licence are stated
            // (proper attribution without asserting a per-file licence string).
            // Falls back to the branded OG card when no photo exists.
            image: (() => {
              const photo = communeImageUrl ?? null
              if (!photo) {
                return `https://www.afficheservices.fr/api/og?title=${encodeURIComponent(city.name)}&subtitle=${encodeURIComponent('Artisans vérifiés · Devis gratuits')}`
              }
              // Special:FilePath/<name>?width=1200 → File:<name> (attribution page)
              const filePage = photo
                .replace('/wiki/Special:FilePath/', '/wiki/File:')
                .replace(/\?.*$/, '')
              return {
                '@type': 'ImageObject',
                contentUrl: photo,
                url: filePage,
                creditText: 'Wikimedia Commons',
                license: filePage,
                acquireLicensePage: filePage,
              }
            })(),
            // Direct Google Maps link for the commune (lat,lon) — reinforces
            // the geographic entity.
            ...(lat && lon ? { hasMap: `https://www.google.com/maps/search/?api=1&query=${lat},${lon}` } : {}),
            // Full geo nesting City → Département → Région → France so Google
            // can place the page precisely in its administrative graph.
            containedInPlace: {
              '@type': 'AdministrativeArea',
              name: city.deptName,
              identifier: city.dept,
              containedInPlace: {
                '@type': 'AdministrativeArea',
                name: city.region,
                containedInPlace: {
                  '@type': 'Country',
                  name: 'France',
                  identifier: 'FR',
                },
              },
            },
          },
          {
            '@type': 'Service',
            '@id': serviceId,
            name: `${service.name} à ${city.name}`,
            serviceType: service.name,
            url: canonicalUrl,
            provider: {
              '@type': 'Organization',
              '@id': 'https://www.afficheservices.fr/#org',
              name: 'AfficheServices',
              url: 'https://www.afficheservices.fr',
            },
            areaServed: {
              '@type': 'City',
              '@id': placeId,
              name: city.name,
              address: {
                '@type': 'PostalAddress',
                addressLocality: city.name,
                postalCode: city.postalCode,
                addressCountry: 'FR',
              },
              geo: {
                '@type': 'GeoCoordinates',
                latitude: lat,
                longitude: lon,
              },
            },
            ...(offers ? { offers } : {}),
            ...(aggregateRating && aggregateRating.count > 0 ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: String(Math.min(5, Math.max(1, aggregateRating.value))),
                reviewCount: String(aggregateRating.count),
                bestRating: '5',
                worstRating: '1',
              },
            } : {}),
          },
        ],
      }}
    />
  )
}
